import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import axios from "axios";
import {
  FaUsers,
  FaChartLine,
  FaTrash,
  FaLock,
  FaUnlock,
  FaMoneyBillWave,
  FaFutbol,
  FaEye,
  FaDatabase,
  FaMagic,
  FaCheckCircle,
  FaTimesCircle,
  FaHistory,
  FaSync,
  FaUpload,
  FaTicketAlt,
  FaPlus,
  FaMinus,
  FaSave,
  FaChartBar,
  FaCalendar,
  FaCoins,
  FaPercentage,
  FaCalculator,
  FaExternalLinkAlt,
  FaArrowUp,
  FaArrowDown,
  FaSearch,
} from "react-icons/fa";
import Layout from "../components/Layout";

// day offset bounds used by the carousels
const MIN_DAY_OFFSET = -30;
const MAX_DAY_OFFSET = 2;

const Admin = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const baseUrl = process.env.REACT_APP_FRONTEND_URL || "https://visionroyale.com";

  // State pentru tabs
  // dayOffset: 0 = today, negative = days ago, positive = days in future
  const [dayOffset, setDayOffset] = useState(0);

  // Left arrow: allow up to 30 days in past
  const prevDay = () => setDayOffset((d) => Math.max(d - 1, -30));
  // Right arrow: allow up to +2 days in future
  const nextDay = () => setDayOffset((d) => Math.min(d + 1, 2));
  const goToToday = () => setDayOffset(0);

  const formatHeaderDate = (offset) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    if (offset === 0) return "Today";
    if (offset === 1) return "Tomorrow";
    if (offset === -1) return "Yesterday";
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  // State pentru tabs
  const [activeTab, setActiveTab] = useState("predictions");
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDailyTicketDisplay, setLoadingDailyTicketDisplay] =
    useState(false);
  const [loadingAvailablePredictions, setLoadingAvailablePredictions] =
    useState(false);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    todayBets: 0,
  });

  const [predictions, setPredictions] = useState([]);
  const [predictionConfig, setPredictionConfig] = useState([]);
  const [selectedDay, setSelectedDay] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [dailyTicket, setDailyTicket] = useState(null);
  const [dailyTicketConfig, setDailyTicketConfig] = useState([]);
  const [selectedTicketDay, setSelectedTicketDay] = useState(0);
  const [generatingTicket, setGeneratingTicket] = useState(false);
  const [publishingTicket, setPublishingTicket] = useState(false);
  const [verifyingTickets, setVerifyingTickets] = useState(false);
  const [ticketStats, setTicketStats] = useState(null);
  const [addingToTicket, setAddingToTicket] = useState(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("");

  const [dbFrom, setDbFrom] = useState("");
  const [dbTo, setDbTo] = useState("");

  const scriptsList = [
    { name: "Refresh Matches", path: "/matches/refresh" },,
    { name: "Update Odds", path: "/update/odds" },
    { name: "Verify Outcomes", path: "/verify-outcomes/run-verification" },
    { name: "Update Standings", path: "/matches/updatestandings" },
    { name: "Update H2H", path: "/update/h2h" },
    { name: "Update Last 10", path: "/update/last10" },
    { name: "Clear Cache", path: "/cleanup/oldmatches" },
  ];

  const [selectedDb, setSelectedDb] = useState(
    scriptsList.reduce((acc, script) => {
      acc[script.path] = "test"; // default database
      return acc;
    }, {})
  );

  const [loadingScripts, setLoadingScripts] = useState({});

  const executeScript = async (scriptPath) => {
    const script_db = selectedDb[scriptPath];
    const url =
      script_db === "test"
        ? `https://api2.visionroyale.com${scriptPath}`
        : `https://api.visionroyale.com${scriptPath}`;
    console.log("Executing:", url);
    try {
      setLoadingScripts((prev) => ({ ...prev, [scriptPath]: true }));

      const response = await axios.get(url, {
        headers: {
          key: process.env.REACT_APP_ACCESKEY,
        },
      });

      console.log("Script executed successfully:", response.data);
      alert(`Script executed successfully: ${response.data.message || "Done"}`);
    } catch (error) {
      console.error("Error executing script:", error);
      alert(`Error executing script: ${error.message}`);
    } finally {
      setLoadingScripts((prev) => ({ ...prev, [scriptPath]: false }));
    }
  };

  // Arrow handlers used in JSX: change selectedDay / selectedTicketDay based on activeTab
  const arrowPrev = () => {
    if (activeTab === "predictions") {
      setSelectedDay((d) => Math.max(d - 1, -30));
    } else if (activeTab === "daily-ticket") {
      setSelectedTicketDay((d) => Math.max(d - 1, -30));
    }
  };

  const arrowNext = () => {
    if (activeTab === "predictions") {
      setSelectedDay((d) => Math.min(d + 1, 2));
    } else if (activeTab === "daily-ticket") {
      setSelectedTicketDay((d) => Math.min(d + 1, 2));
    }
  };

  const getActiveSelected = () =>
    activeTab === "predictions" ? selectedDay : selectedTicketDay;

  const goToTodayForActive = () => {
    if (activeTab === "predictions") setSelectedDay(0);
    else if (activeTab === "daily-ticket") setSelectedTicketDay(0);
  };
  // refs for the horizontal scrollers
  const predictionsScrollerRef = useRef(null);
  const ticketsScrollerRef = useRef(null);

  const scrollToOffsetIn = (scrollerRef, offset) => {
    if (!scrollerRef?.current) return;
    try {
      const el = scrollerRef.current.querySelector(`[data-offset="${offset}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      } else {
        // fallback: approximate scrollLeft
        const children = scrollerRef.current.children;
        for (let i = 0; i < children.length; i++) {
          if (children[i].dataset && children[i].dataset.offset == offset) {
            const child = children[i];
            const parent = scrollerRef.current;
            const left =
              child.offsetLeft - parent.clientWidth / 2 + child.clientWidth / 2;
            parent.scrollTo({ left, behavior: "smooth" });
            break;
          }
        }
      }
    } catch (e) {
      // ignore
    }
  };

  // În Admin component, la început
  useEffect(() => {
    // Restaurează selectedTicketDay din localStorage la montare
    const savedTicketDay = localStorage.getItem("selectedTicketDay");
    if (savedTicketDay !== null) {
      setSelectedTicketDay(parseInt(savedTicketDay));
    }
  }, []);

  // Salvează selectedTicketDay în localStorage când se schimbă
  useEffect(() => {
    localStorage.setItem("selectedTicketDay", selectedTicketDay.toString());
  }, [selectedTicketDay]);

  // La schimbarea tab-ului, salvează și selectedDay
  useEffect(() => {
    if (activeTab === "predictions") {
      localStorage.setItem("selectedDay", selectedDay.toString());
    }
  }, [selectedDay, activeTab]);

  // scroll when selected changes
  useEffect(() => {
    scrollToOffsetIn(predictionsScrollerRef, selectedDay);
  }, [selectedDay]);

  useEffect(() => {
    scrollToOffsetIn(ticketsScrollerRef, selectedTicketDay);
  }, [selectedTicketDay]);

  // Ensure carousel scrolls to "Today" when switching tabs or when Admin mounts
  useEffect(() => {
    // small delay to allow tab contents to render
    if (activeTab === "predictions") {
      setTimeout(
        () => scrollToOffsetIn(predictionsScrollerRef, selectedDay),
        80,
      );
    } else if (activeTab === "daily-ticket") {
      setTimeout(
        () => scrollToOffsetIn(ticketsScrollerRef, selectedTicketDay),
        80,
      );
    }
  }, [activeTab]);

  const [availablePredictions, setAvailablePredictions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMarket, setSelectedMarket] = useState("all");
  const [dailyTicketCombos, setDailyTicketCombos] = useState([]);
  const [dailyTicketComboIndex, setDailyTicketComboIndex] = useState(0);
  const [activeDailyTicket, setActiveDailyTicket] = useState(null);
  const [loadingDailyTicket, setLoadingDailyTicket] = useState(false);

  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState("");
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [editingAssignedId, setEditingAssignedId] = useState(null);
  const [assignedValue, setAssignedValue] = useState("");

  const [commentInputs, setCommentInputs] = useState({});
  const [submittingComment, setSubmittingComment] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "low",
    assigned_to: "",
  });
  const [editForm, setEditForm] = useState({
    description: "",
    assigned_to: "",
  });

  const [selectedImage, setSelectedImage] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    assigned_to: "all",
    status: "all",
    priority: "all",
    date: "all",
  });

  const computeBestCombinations = (candidates = [], opts = {}) => {
    const target = opts.target || 5;
    const minOdd = opts.minOdd ?? 4.0;
    const maxOdd = opts.maxOdd ?? 6.0;
    const maxCandidates = opts.maxCandidates || 12;
    const sizes = opts.sizes || [2, 3, 4];

    const filtered = (candidates || [])
      .filter((c) => c.odd && Number(c.odd) > 1.01)
      .map((c) => ({
        ...c,
        odd: Number(c.odd),
        winProb: Number(
          c.win_probability ?? c.winProbability ?? c.win_prob ?? 0,
        ),
        score: Number(c.value_score ?? c.valueBetScore ?? 0),
      }))
      .sort((a, b) => (b.score || b.winProb) - (a.score || a.winProb))
      .slice(0, maxCandidates);

    const combos = [];
    const recurse = (start, path, size) => {
      if (path.length === size) {
        const odd = path.reduce((p, x) => p * x.odd, 1);
        const winFrac = path.reduce(
          (p, x) => p * (Math.max(0, Math.min(100, x.winProb)) / 100),
          1,
        );
        const winPercent = winFrac * 100;
        const inRange = odd >= minOdd && odd <= maxOdd;
        const distance = Math.abs(odd - target);
        const score = inRange
          ? winPercent + 1000 - distance
          : winPercent - distance * 10;
        combos.push({
          picks: path.map((x) => ({ ...x })),
          odd,
          winPercent,
          score,
        });
        return;
      }
      for (let i = start; i < filtered.length; i++) {
        recurse(i + 1, path.concat(filtered[i]), size);
        if (combos.length > 3000) break;
      }
    };

    for (const s of sizes) {
      recurse(0, [], s);
      if (combos.length > 2000) break;
    }

    combos.sort((a, b) => b.score - a.score);
    return combos.slice(0, 50);
  };

  const generateDailyTicketPreview = async (offset = 0) => {
    if (offset < 0) {
      // historical — nothing to preview client-side
      setDailyTicketCombos([]);
      setActiveDailyTicket(null);
      return;
    }
    setLoadingDailyTicket(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket/available-predictions/${offset}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const candidates = res.data.predictions || [];
      if (!candidates.length) {
        setDailyTicketCombos([]);
        setActiveDailyTicket(null);
        setLoadingDailyTicket(false);
        return;
      }
      const combos = computeBestCombinations(candidates, {
        target: 5,
        minOdd: 4.0,
        maxOdd: 6.0,
        maxCandidates: 12,
        sizes: [2, 3, 4],
      });
      setDailyTicketCombos(combos);
      setDailyTicketComboIndex(0);
      setActiveDailyTicket(combos[0] || null);
    } catch (err) {
      console.error("generateDailyTicketPreview error:", err);
      setDailyTicketCombos([]);
      setActiveDailyTicket(null);
    } finally {
      setLoadingDailyTicket(false);
    }
  };

  const regenerateDailyTicketPreview = () => {
    if (!dailyTicketCombos || dailyTicketCombos.length === 0) return;
    const next = (dailyTicketComboIndex + 1) % dailyTicketCombos.length;
    setDailyTicketComboIndex(next);
    setActiveDailyTicket(dailyTicketCombos[next]);
  };

  const refreshTicketData = async (dayOffset) => {
    try {
      // Reîncarcă biletul
      await fetchDailyTicketForDay(dayOffset);

      // Reîncarcă predicțiile disponibile (doar pentru zilele viitoare)
      if (dayOffset >= 0) {
        await fetchAvailablePredictions(dayOffset);
      }

      // Dacă ești în tab-ul daily-ticket, forțează re-render
      if (activeTab === "daily-ticket") {
        setDailyTicket((prev) => ({ ...prev })); // Forțează re-render
      }
    } catch (error) {
      console.error("Error refreshing ticket data:", error);
    }
  };

  // --- end preview helpers ---

  // Verifică dacă utilizatorul este admin
  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate("/");
    }
  }, [user, navigate]);

  // Încarcă datele în funcție de tab-ul activ
  useEffect(() => {
    if (activeTab === "users") {
      fetchAllUsers();
    } else if (activeTab === "transactions") {
      fetchTransactions();
    } else if (activeTab === "predictions") {
      fetchPredictionConfig();
      fetchPredictionsForDay(selectedDay);
    } else if (activeTab === "daily-ticket") {
      fetchDailyTicketConfig();
      fetchDailyTicketForDay(selectedTicketDay);
      fetchDailyTicketStats();
      // Încarcă predicțiile disponibile pentru a le selecta
      fetchAvailablePredictions(selectedTicketDay);
    } else if (activeTab === "tasks") {
      fetchTasks();
    }
  }, [activeTab, selectedDay, selectedTicketDay]);

  // Încarcă statisticile la montare
  useEffect(() => {
    fetchStats();
  }, []);

  // =============== FUNCTII GENERALE ===============
  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/transactions`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data.stats) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // =============== FUNCTII TASKS ===============

  /* Fetch Task */

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/tasks`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTasks(response.data || []);
    } catch (error) {
      console.error(
        "Error fetching tasks:",
        error
      );
    }
  };

  /* Create Task */

  const createTask = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) return;

    try {
      setLoading(true);

      const token =
        localStorage.getItem("token");

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/tasks`,
        {
          title: form.title,
          description: form.description,
          priority: form.priority,
          assigned_to:
            form.assigned_to || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTasks((prev) => [
        {
          ...response.data,
          comments: [],
          attachments: [],
        },
        ...prev,
      ]);

      setForm({
        title: "",
        description: "",
        priority: "low",
        assigned_to: "",
      });
    } catch (error) {
      console.error(
        "Error creating task:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  /* Update Task */

  const updateTask = async (
    id,
    updates
  ) => {
    try {
      const token =
        localStorage.getItem("token");

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/tasks/${id}`,
        updates,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTasks((prev) =>
        prev.map((task) =>
          task.id === id
            ? response.data
            : task
        )
      );
    } catch (error) {
      console.error(
        "Error updating task:",
        error
      );
    }
  };

  /* Delete Task */

  const deleteTask = async (id) => {
    try {
      const token =
        localStorage.getItem("token");

      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/tasks/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTasks((prev) =>
        prev.filter((task) => task.id !== id)
      );
    } catch (error) {
      console.error(
        "Error deleting task:",
        error
      );
    }
  };

  /* Upload Attachments */

  const uploadAttachment = async (taskId, file) => {
    try {
      const token = localStorage.getItem("token");

      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/tasks/${taskId}/attachments`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      const newAttachment = res.data.attachment;

      setTasks((prev) =>
        prev.map((task) => {
          if (task.id !== taskId) return task;

          return {
            ...task,
            attachments: [
              ...(task.attachments || []),
              newAttachment,
            ],
          };
        })
      );
    } catch (err) {
      console.error("Upload failed:", err);
    }
  };

  /* Delete Attachment */
  const deleteAttachment = async (
    taskId,
    attachmentId
  ) => {
    try {
      const token = localStorage.getItem("token");

      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/tasks/attachments/${attachmentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTasks((prev) =>
        prev.map((task) => {
          if (task.id !== taskId) return task;

          return {
            ...task,
            attachments:
              task.attachments.filter(
                (a) => a.id !== attachmentId
              ),
          };
        })
      );
    } catch (err) {
      console.error(
        "Delete attachment failed:",
        err
      );
    }
  };

  /* Add Comment */

  const addComment = async (taskId) => {
    const comment =
      commentInputs[taskId]?.trim();

    if (!comment) return;

    try {
      setSubmittingComment(true);

      const token =
        localStorage.getItem("token");

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/tasks/${taskId}/comments`,
        {
          comment,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTasks((prev) =>
        prev.map((task) => {
          if (task.id !== taskId) return task;

          return {
            ...task,
            comments: [
              ...(task.comments || []),
              res.data,
            ],
          };
        })
      );

      setCommentInputs((prev) => ({
        ...prev,
        [taskId]: "",
      }));
    } catch (err) {
      console.error(
        "Failed to add comment:",
        err
      );
    } finally {
      setSubmittingComment(false);
    }
  };

  /* Delete Comment */

  const deleteComment = async (
    taskId,
    commentId
  ) => {
    try {
      const token =
        localStorage.getItem("token");

      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/tasks/comments/${commentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTasks((prev) =>
        prev.map((task) => {
          if (task.id !== taskId) return task;

          return {
            ...task,
            comments: task.comments.filter(
              (c) => c.id !== commentId
            ),
          };
        })
      );
    } catch (err) {
      console.error(
        "Failed to delete comment:",
        err
      );
    }
  };

  /* Paste handler */

  const handlePaste = async (e, taskId) => {
    const items = e.clipboardData?.items;

    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image")) {
        const file = item.getAsFile();

        if (file) {
          await uploadAttachment(taskId, file);
        }
      }
    }
  };

  /* Edit Task */

  const startEditing = (task) => {
    setEditingTaskId(task.id);
    setEditDescription(task.description || "");
  };

  const saveDescription = async (taskId) => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/tasks/${taskId}`,
        {
          description: editDescription,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTasks((prev) =>
        prev.map((t) => {
          if (t.id !== taskId) return t;

          return {
            ...t,
            ...res.data,
            attachments:
              res.data.attachments ||
              t.attachments,
          };
        })
      );

      setEditingTaskId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const startEditAssigned = (task) => {
    setEditingAssignedId(task.id);
    setAssignedValue(task.assigned_to || "");
  };

  const saveAssignedTo = async (taskId) => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/tasks/${taskId}`,
        {
          assigned_to: assignedValue,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTasks((prev) =>
        prev.map((t) => {
          if (t.id !== taskId) return t;

          return {
            ...t,
            ...res.data,
            attachments:
              res.data.attachments ||
              t.attachments,
          };
        })
      );

      setEditingAssignedId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const cycleStatus = (current) => {
    const order = ["new", "in_progress", "done"];
    const index = order.indexOf(current);

    return order[(index + 1) % order.length];
  };

  const cyclePriority = (current) => {
    const order = ["low", "medium", "high"];
    const index = order.indexOf(current);

    return order[(index + 1) % order.length];
  };

  /*
  |--------------------------------------------------------------------------
  | COLORS
  |--------------------------------------------------------------------------
  */

  function statusColor(status) {
    switch (status) {
      case "new":
        return "bg-slate-600";

      case "in_progress":
        return "bg-yellow-600";

      case "done":
        return "bg-green-600";

      default:
        return "bg-slate-600";
    }
  }

  function priorityColor(priority) {
    switch (priority) {
      case "high":
        return `
          bg-red-500/20
          text-red-400
          border-red-500/30
        `;

      case "medium":
        return `
          bg-yellow-500/20
          text-yellow-400
          border-yellow-500/30
        `;

      default:
        return `
          bg-slate-500/20
          text-slate-300
          border-slate-500/30
        `;
    }
  }

  // =============== FUNCTII PREDICTII ===============
  const fetchPredictionConfig = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/prediction-config`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setPredictionConfig(response.data.config || []);
    } catch (error) {
      console.error("Error fetching prediction config:", error);
    }
  };

  const fetchPredictionsForDay = async (dayOffset) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/predictions/${dayOffset}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setPredictions(response.data.predictions || []);
    } catch (error) {
      console.error("Error fetching predictions:", error);
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  const generatePredictions = async (dayOffset) => {
    if (dayOffset < 0) {
      fetchPredictionsForDay(dayOffset);
      return;
    }

    if (
      !window.confirm(
        `Generate predictions for ${getDayDisplayName(dayOffset)}?`,
      )
    ) {
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/generate-predictions`,
        { dayOffset, maxMatches: 200 },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      alert(`✅ ${response.data.message}`);
      fetchPredictionsForDay(dayOffset);
    } catch (error) {
      console.error("Error generating predictions:", error);
      alert(
        "❌ Error generating predictions: " +
          (error.response?.data?.error || error.message),
      );
    } finally {
      setGenerating(false);
    }
  };

  const updatePredictionStatus = async (id, status) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/predictions/${id}/status`,
        { status },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setPredictions(
        predictions.map((p) => (p.id === id ? { ...p, status } : p)),
      );
    } catch (error) {
      console.error("❌ Error updating prediction status:", error);
      alert(
        "❌ Error updating status: " +
          (error.response?.data?.error || error.message),
      );
    }
  };

  const updateDayConfig = async (dayOffset, updates) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/prediction-config/${dayOffset}`,
        updates,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      fetchPredictionConfig();
    } catch (error) {
      console.error("Error updating config:", error);
      alert("❌ Error updating configuration");
    }
  };

  const publishAllForDay = async (dayOffset) => {
    if (
      !window.confirm(
        `Publish ALL draft predictions for ${getDayDisplayName(dayOffset)}?`,
      )
    ) {
      return;
    }

    setPublishing(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/publish-day/${dayOffset}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      alert(`✅ ${response.data.message}`);
      fetchPredictionsForDay(dayOffset);
    } catch (error) {
      console.error("Error publishing predictions:", error);
      alert("❌ Error publishing predictions");
    } finally {
      setPublishing(false);
    }
  };

  const verifyPredictions = async () => {
    if (!window.confirm("Verify all finished predictions?")) {
      return;
    }

    setVerifying(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/verify-predictions`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      alert(
        `✅ ${response.data.message}\nWon: ${response.data.stats.won}\nLost: ${response.data.stats.lost}\nVoid: ${response.data.stats.void}`,
      );
      fetchPredictionsForDay(selectedDay);
    } catch (error) {
      console.error("Error verifying predictions:", error);
      alert(
        "❌ Error verifying predictions: " +
          (error.response?.data?.error || error.message),
      );
    } finally {
      setVerifying(false);
    }
  };

  // =============== FUNCTII Daily Value Snapshot ===============
  const fetchDailyTicketConfig = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket-config`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setDailyTicketConfig(response.data.config || []);
    } catch (error) {
      console.error("Error fetching Daily Value Snapshot config:", error);
    }
  };

  const fetchDailyTicketForDay = async (dayOffset) => {
    setLoadingDailyTicketDisplay(true); // SCHIMBĂ AICI
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket/${dayOffset}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        setDailyTicket(response.data.ticket);
      } else {
        setDailyTicket(null);
      }
    } catch (error) {
      console.error("Error fetching Daily Value Snapshot:", error);
      setDailyTicket(null);
    } finally {
      setLoadingDailyTicketDisplay(false); // SCHIMBĂ AICI
    }
  };

  const fetchAvailablePredictions = async (dayOffset) => {
    if (dayOffset < 0) return;

    setLoadingAvailablePredictions(true); // SCHIMBĂ AICI
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket/available-predictions/${dayOffset}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        setAvailablePredictions(response.data.predictions || []);
      } else {
        setAvailablePredictions([]);
      }
    } catch (error) {
      console.error("Error fetching available predictions:", error);
      setAvailablePredictions([]);
    } finally {
      setLoadingAvailablePredictions(false); // SCHIMBĂ AICI
    }
  };

  const fetchDailyTicketStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket-stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        setTicketStats(response.data);
      }
    } catch (error) {
      console.error("Error fetching Daily Value Snapshot stats:", error);
    }
  };

  const generateDailyTicket = async (dayOffset) => {
    if (dayOffset < 0) {
      fetchDailyTicketForDay(dayOffset);
      return;
    }

    if (
      !window.confirm(
        `Generate Daily Value Snapshot for ${getDayDisplayName(dayOffset)}?`,
      )
    ) {
      return;
    }

    setGeneratingTicket(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/generate-daily-ticket`,
        { dayOffset, stake: 10.0 },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        fetchDailyTicketForDay(dayOffset);
        fetchAvailablePredictions(dayOffset); // Reîncarcă predicțiile disponibile
      }
    } catch (error) {
      console.error("Error generating Daily Value Snapshot:", error);
      alert(
        "❌ Error generating ticket: " +
          (error.response?.data?.error || error.message),
      );
    } finally {
      setGeneratingTicket(false);
    }
  };

  const addMatchToTicket = async (predictionId) => {
    if (!window.confirm("Add this match to the Daily Value Snapshot?")) {
      return;
    }

    setAddingToTicket(true);
    try {
      const token = localStorage.getItem("token");
      console.log("🔄 [DEBUG] Adding match:", {
        day: selectedTicketDay,
        predictionId,
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket/add-match`,
        { dayOffset: selectedTicketDay, predictionId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      console.log("✅ [DEBUG] API Response:", response.data);

      if (response.data.success) {
        // ACTUALIZARE CRITICĂ: Primește întregul bilet actualizat din backend
        if (response.data.ticket) {
          setDailyTicket(response.data.ticket);
        } else {
          // Fallback: reîncarcă biletul de la server
          fetchDailyTicketForDay(selectedTicketDay);
        }

        // Actualizează predicțiile disponibile pentru a marca ca adăugat
        setAvailablePredictions((prev) =>
          prev.map((p) => (p.id === predictionId ? { ...p, in_ticket: 1 } : p)),
        );

        alert(`✅ ${response.data.message}`);
      }
    } catch (error) {
      console.error("❌ [DEBUG] Error:", error.response?.data || error.message);
      alert(
        "❌ Error adding match: " +
          (error.response?.data?.error || error.message),
      );
    } finally {
      setAddingToTicket(false);
    }
  };

  const removeMatchFromTicket = async (matchId) => {
    if (!window.confirm("Remove this match from the Daily Value Snapshot?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket/remove-match/${matchId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        alert(`✅ ${response.data.message}`);

        // Reîncarcă biletul complet de la server
        fetchDailyTicketForDay(selectedTicketDay);

        // Reîncarcă predicțiile disponibile
        fetchAvailablePredictions(selectedTicketDay);
      }
    } catch (error) {
      console.error("Error removing match from ticket:", error);
      alert(
        "❌ Error removing match: " +
          (error.response?.data?.error || error.message),
      );
    }
  };

  const publishDailyTicket = async (dayOffset) => {
    if (
      !window.confirm(
        `Publish Daily Value Snapshot for ${getDayDisplayName(dayOffset)}?`,
      )
    ) {
      return;
    }

    setPublishingTicket(true);
    try {
      const token = localStorage.getItem("token");

      // Salvează meciurile curente
      const currentMatches = dailyTicket?.matches || [];

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket/publish/${dayOffset}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        alert(`✅ ${response.data.message}`);

        // Actualizează local ticket-ul cu status "published" dar păstrează meciurile
        setDailyTicket((prev) => ({
          ...prev,
          status: "published",
          published_at: new Date().toISOString(),
          matches: currentMatches, // Păstrează meciurile existente
        }));
      }
    } catch (error) {
      console.error("Error publishing Daily Value Snapshot:", error);
      alert(
        "❌ Error publishing ticket: " +
          (error.response?.data?.error || error.message),
      );
    } finally {
      setPublishingTicket(false);
    }
  };

  const unpublishDailyTicket = async (dayOffset) => {
    if (
      !window.confirm(
        `Unpublish Daily Value Snapshot for ${getDayDisplayName(dayOffset)}?`,
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket/unpublish/${dayOffset}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        fetchDailyTicketForDay(dayOffset);
      }
    } catch (error) {
      console.error("Error unpublishing Daily Value Snapshot:", error);
      alert(
        "❌ Error unpublishing ticket: " +
          (error.response?.data?.error || error.message),
      );
    }
  };

  const deleteDailyTicket = async (dayOffset) => {
    if (
      !window.confirm(
        `Delete Daily Value Snapshot for ${getDayDisplayName(dayOffset)}?`,
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket/${dayOffset}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        fetchDailyTicketForDay(dayOffset);
        fetchAvailablePredictions(dayOffset);
      }
    } catch (error) {
      console.error("Error deleting Daily Value Snapshot:", error);
      alert(
        "❌ Error deleting ticket: " +
          (error.response?.data?.error || error.message),
      );
    }
  };

  const verifyDailyTickets = async () => {
    if (!window.confirm("Verify all past Daily Value Snapshots?")) {
      return;
    }

    setVerifyingTickets(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/verify-daily-tickets`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        alert(
          `✅ ${response.data.message}\nVerified: ${response.data.stats.verified}\nWon: ${response.data.stats.won}\nLost: ${response.data.stats.lost}`,
        );
        fetchDailyTicketStats();
        fetchDailyTicketForDay(selectedTicketDay);
      }
    } catch (error) {
      console.error("Error verifying Daily Value Snapshots:", error);
      alert(
        "❌ Error verifying tickets: " +
          (error.response?.data?.error || error.message),
      );
    } finally {
      setVerifyingTickets(false);
    }
  };

  const updateDailyTicketConfig = async (dayOffset, updates) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket-config/${dayOffset}`,
        updates,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      fetchDailyTicketConfig();
    } catch (error) {
      console.error("Error updating Daily Value Snapshot config:", error);
      alert("❌ Error updating configuration");
    }
  };

  const verifyDailyTicketForDay = async (dayOffset) => {
    if (
      !window.confirm(
        `Verify Daily Value Snapshot for ${getDayDisplayName(dayOffset)}?`,
      )
    ) {
      return;
    }
    setVerifyingTickets(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/verify-daily-ticket/${dayOffset}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data.success) {
        alert(
          `✅ ${response.data.message}\nVerified: ${response.data.verified}`,
        );
        fetchDailyTicketStats();
        fetchDailyTicketForDay(dayOffset);
      } else {
        alert("❌ " + (response.data.message || "Verification failed"));
      }
    } catch (error) {
      console.error("Error verifying Daily Value Snapshot:", error);
      alert(
        "❌ Error verifying ticket: " +
          (error.response?.data?.error || error.message),
      );
    } finally {
      setVerifyingTickets(false);
    }
  };

  // =============== FUNCTII Manager ===============
  const createUser = async () => {
    setLoading(true);

    if (!username || !email || !password) {
      alert("Username, email and password are required");
      setLoading(false);
      return;
    }

    if (!email.includes("@")) {
      alert("Invalid email");
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/create-user`,
        {
          username,
          email,
          password,
          tier,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      console.log(response.data);

      setUsername("");
      setEmail("");
      setPassword("");
      setTier("");
      alert("User created.");
    } catch (error) {
      console.error("Error creating user:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyDB = async () => {
    setLoading(true);

    if (!dbFrom || !dbTo) {
      alert("both are required");
      setLoading(false);
      return;
    }

    if (dbTo === "og") {
      alert("DON'T COPY TO OG!");
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/dbmgr/copy`,
        {
          from: dbFrom,
          to: dbTo,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-key": "gicu123",
          },
        },
      );

      console.log(response.data);

      setDbFrom("");
      setDbTo("");
      alert("DB copied.");
    } catch (error) {
      console.error("Error copying:", error);
    } finally {
      setLoading(false);
    }
  };

  // =============== FUNCTII HELPER ===============
  const getDayDisplayName = (offset) => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + offset);

    const dayName = days[targetDate.getDay()];
    const dateStr = targetDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    if (offset === 0) return "Today";
    return `${dayName}, ${dateStr}`;
  };

  const calculateValueBetScore = (odd, winProbability) => {
    const oddNum = typeof odd === "string" ? parseFloat(odd) : odd || 0;
    const winProbNum =
      typeof winProbability === "string"
        ? parseFloat(winProbability)
        : winProbability || 0;

    if (!oddNum || !winProbNum) return 0;

    const impliedProbability = 100 / oddNum;
    const value =
      ((winProbNum - impliedProbability) / impliedProbability) * 100;
    return Math.max(0, value.toFixed(1));
  };

  const calculateMargin = (odd, winProbability) => {
    const oddNum = typeof odd === "string" ? parseFloat(odd) : odd || 0;
    const winProbNum =
      typeof winProbability === "string"
        ? parseFloat(winProbability)
        : winProbability || 0;

    if (!oddNum || !winProbNum) return 0;

    const impliedProbability = 100 / oddNum;
    return (winProbNum - impliedProbability).toFixed(1);
  };

  const calculateBreakEven = (odd) => {
    const oddNum = typeof odd === "string" ? parseFloat(odd) : odd || 0;
    if (!oddNum) return 0;
    return (100 / oddNum).toFixed(1);
  };

  // Funcții pentru styling (la fel ca în premium)
  const getValueBetColor = (score) => {
    const scoreNum = typeof score === "string" ? parseFloat(score) : score || 0;
    if (scoreNum >= 15) return "bg-green-500";
    if (scoreNum >= 10) return "bg-green-400";
    if (scoreNum >= 5) return "bg-yellow-400";
    return "bg-yellow-300";
  };

  const getValueBetRating = (score) => {
    const scoreNum = typeof score === "string" ? parseFloat(score) : score || 0;
    if (scoreNum >= 15)
      return { text: "EXCELLENT", color: "text-green-700", bg: "bg-green-100" };
    if (scoreNum >= 10)
      return { text: "GOOD", color: "text-green-600", bg: "bg-green-50" };
    if (scoreNum >= 5)
      return { text: "FAIR", color: "text-yellow-600", bg: "bg-yellow-50" };
    return { text: "LOW", color: "text-yellow-700", bg: "bg-yellow-100" };
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: "bg-gray-500", text: "Draft" },
      published: { color: "bg-blue-500", text: "Published" },
      won: { color: "bg-green-600", text: "WON" },
      lost: { color: "bg-red-500", text: "LOST" },
      void: { color: "bg-gray-600", text: "VOID" },
    };
    const config = statusConfig[status] || {
      color: "bg-gray-400",
      text: status,
    };
    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getDayConfig = (offset) => {
    return (
      predictionConfig.find((c) => c.day_offset === offset) || {
        status: "show",
        max_predictions: 100,
      }
    );
  };

  const getDailyTicketConfig = (offset) => {
    return (
      dailyTicketConfig.find((c) => c.day_offset === offset) || {
        status: "hide",
        max_matches: 5,
        min_odd: 4,
        max_odd: 6,
      }
    );
  };

  const openMatchPage = (matchId) => {
    if (matchId) {
      window.open(`${baseUrl}/match/${matchId}`, "_blank");
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  // Filtrare predicții disponibile
  const filteredPredictions = availablePredictions.filter((prediction) => {
    const matchesSearch =
      searchTerm === "" ||
      prediction.home_team?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prediction.away_team?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prediction.league?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesMarket =
      selectedMarket === "all" ||
      prediction.market_type?.includes(selectedMarket);

    return matchesSearch && matchesMarket;
  });

  // =============== COMPONENTE ===============

  const displayMarketName = (marketType, selection) => {
    // Cazuri speciale pentru noile tipuri
    if (marketType === "Total - Home" && selection === "Over 1.5") {
      return "Total Home Over 1.5";
    }
    if (marketType === "Total - Away" && selection === "Over 1.5") {
      return "Total Away Over 1.5";
    }

    // Cazuri existente
    if (marketType === "Match Winner") {
      return `Match Winner - ${selection}`;
    }
    if (marketType === "Goals Over/Under") {
      return `Goals ${selection}`;
    }
    if (marketType === "Both Teams To Score") {
      return `Both Teams Score - ${selection}`;
    }
    if (marketType === "Total - Home") {
      return `Total Home - ${selection}`;
    }
    if (marketType === "Total - Away") {
      return `Total Away - ${selection}`;
    }

    // Default
    return `${marketType} - ${selection}`;
  };

  const PredictionCard = ({ prediction }) => {
    // Calculează metricile
    const valueScore = calculateValueBetScore(
      prediction.odd,
      prediction.win_probability,
    );
    const margin = calculateMargin(prediction.odd, prediction.win_probability);
    const breakEven = calculateBreakEven(prediction.odd);

    // Folosește margin ca valueScore (așa cum ai cerut)
    const displayValueScore = Math.abs(margin); // valoarea pozitivă a margin
    const isPositiveMargin = margin > 0;

    // Extrage scorurile finale
    const finalHomeGoals = prediction.goals_home;
    const finalAwayGoals = prediction.goals_away;

    // Verifică dacă există scor final
    const hasFinalResult = finalHomeGoals !== null && finalAwayGoals !== null;
    const finalScore = hasFinalResult
      ? `${finalHomeGoals} - ${finalAwayGoals}`
      : null;

    // Verifică dacă meciul s-a terminat
    const isMatchFinished =
      prediction.match_status &&
      ["FT", "AET", "PEN", "FT_PEN"].includes(prediction.match_status);

    const getCardBgColor = () => {
      switch (prediction.status) {
        case "won":
          return "bg-green-50";
        case "lost":
          return "bg-red-50";
        case "published":
          return "bg-blue-50";
        default:
          return "bg-white";
      }
    };

    const getBorderColor = () => {
      switch (prediction.status) {
        case "won":
          return "border-green-200";
        case "lost":
          return "border-red-200";
        case "published":
          return "border-blue-200";
        default:
          return "border-gray-200";
      }
    };

    // Funcție pentru culoarea barei Value Score
    const getValueScoreBarColor = () => {
      if (margin >= 15) return "bg-green-500";
      if (margin >= 10) return "bg-green-400";
      if (margin >= 5) return "bg-yellow-400";
      return "bg-yellow-300";
    };

    return (
      <div
        className={`rounded-lg border p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${getCardBgColor()} ${getBorderColor()}`}
        onClick={() => openMatchPage(prediction.match_id)}
      >
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold bg-gray-100 text-black px-2 py-1 rounded">
              {prediction.league || "Unknown"}
            </span>
            <span className="text-xs text-black">
              {formatTime(prediction.match_date)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {getStatusBadge(prediction.status)}
          </div>
        </div>

        {/* Echipe și scor */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-center flex-1">
              <h3 className="font-bold text-black text-sm truncate">
                {prediction.home_team}
              </h3>
            </div>

            <div className="mx-2 flex flex-col items-center">
              {hasFinalResult ? (
                <>
                  {/* Afișează scorul final */}
                  <div className="text-2xl font-bold text-black">
                    {finalHomeGoals} - {finalAwayGoals}
                  </div>
                  <div className="text-xs text-black mt-1">
                    {prediction.match_status || "FT"}
                  </div>
                </>
              ) : isMatchFinished ? (
                <>
                  {/* Meci terminat dar fără scor? */}
                  <div className="text-lg font-bold text-black">N/A - N/A</div>
                  <div className="text-xs text-black">
                    {prediction.match_status}
                  </div>
                </>
              ) : (
                /* Meci încă nu s-a jucat */
                <div className="px-2 py-1 bg-gray-100 rounded">
                  <span className="text-xs font-bold text-black">VS</span>
                </div>
              )}
            </div>

            <div className="text-center flex-1">
              <h3 className="font-bold text-black text-sm truncate">
                {prediction.away_team}
              </h3>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* MARKET TYPE și SELECTION */}
          <div className="text-center mb-1">
            <div className="text-sm font-semibold text-black">
              {prediction.market_type}
            </div>
            <div className="text-sm font-bold text-black mt-1">
              {prediction.selection}
            </div>
          </div>

          {/* ODD & WIN % */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">ODD</div>
              <div className="text-lg font-bold text-black">
                {prediction.odd}
              </div>
            </div>
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">WIN %</div>
              <div className="text-lg font-bold text-black">
                {prediction.win_probability}%
              </div>
            </div>
          </div>

          {/* VALUE SCORE BAR - între carduri */}
          <div className="space-y-1 pt-1">
            <div className="flex justify-between text-xs">
              <span className="font-semibold text-black">VALUE SCORE</span>
              <span className="font-bold text-black">
                {displayValueScore.toFixed(1)}
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`${getValueScoreBarColor()} h-full transition-all duration-300`}
                style={{
                  width: `${Math.min(100, displayValueScore * 4)}%`, // Scalam pentru a arăta bine
                }}
              />
            </div>
          </div>

          {/* MARGIN & BREAK-EVEN */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">MARGIN</div>
              <div
                className={`text-sm font-bold ${
                  margin > 0 ? "text-black" : "text-black"
                }`}
              >
                {margin > 0 ? "+" : ""}
                {margin}%
              </div>
            </div>
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">BREAK-EVEN</div>
              <div className="text-sm font-bold text-black">{breakEven}%</div>
            </div>
          </div>

          {!hasFinalResult && (
            <div className="flex gap-2 pt-2">
              {prediction.status === "draft" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updatePredictionStatus(prediction.id, "published");
                  }}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded text-xs text-white flex-1"
                >
                  Publish
                </button>
              )}

              {prediction.status === "published" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updatePredictionStatus(prediction.id, "draft");
                  }}
                  className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 rounded text-xs text-white flex-1"
                >
                  Unpublish
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const AvailablePredictionCard = ({ prediction }) => {
    // Calculează metricile
    const margin = calculateMargin(prediction.odd, prediction.win_probability);
    const breakEven = calculateBreakEven(prediction.odd);

    // Folosește margin ca valueScore
    const displayValueScore = Math.abs(margin);

    const isInTicket = prediction.in_ticket === 1;

    // Funcție pentru culoarea barei Value Score
    const getValueScoreBarColor = () => {
      if (margin >= 15) return "bg-green-500";
      if (margin >= 10) return "bg-green-400";
      if (margin >= 5) return "bg-yellow-400";
      return "bg-yellow-300";
    };

    return (
      <div
        className={`rounded-lg border p-4 shadow-sm transition-all ${
          isInTicket
            ? "bg-gray-100 border-gray-300 opacity-75"
            : "bg-white border-gray-200 hover:shadow-md hover:border-blue-300"
        }`}
      >
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold bg-gray-100 text-black px-2 py-1 rounded">
              {prediction.league || "Unknown"}
            </span>
            <span className="text-xs text-black">
              {formatTime(prediction.match_date)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {isInTicket ? (
              <span className="px-2 py-1 rounded text-xs bg-green-500 text-white">
                In Ticket
              </span>
            ) : (
              <span className="px-2 py-1 rounded text-xs bg-blue-500 text-white">
                Available
              </span>
            )}
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              <h3 className="font-bold text-black text-sm truncate">
                {prediction.homeTeam || prediction.home_team}
              </h3>
            </div>
            <div className="mx-2 px-2 py-1 bg-gray-100 rounded">
              <span className="text-xs font-bold text-black">VS</span>
            </div>
            <div className="text-center flex-1">
              <h3 className="font-bold text-black text-sm truncate">
                {prediction.awayTeam || prediction.away_team}
              </h3>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* MARKET TYPE și SELECTION */}
          <div className="text-center mb-1">
            <div className="text-sm font-semibold text-black">
              {prediction.market_type}
            </div>
            <div className="text-sm font-bold text-black mt-1">
              {prediction.selection}
            </div>
          </div>

          {/* ODD & WIN % */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">ODD</div>
              <div className="text-lg font-bold text-black">
                {prediction.odd}
              </div>
            </div>
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">WIN %</div>
              <div className="text-lg font-bold text-black">
                {prediction.win_probability}%
              </div>
            </div>
          </div>

          {/* VALUE SCORE BAR */}
          <div className="space-y-1 pt-1">
            <div className="flex justify-between text-xs">
              <span className="font-semibold text-black">VALUE SCORE</span>
              <span className="font-bold text-black">
                {displayValueScore.toFixed(1)}
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`${getValueScoreBarColor()} h-full transition-all duration-300`}
                style={{
                  width: `${Math.min(100, displayValueScore * 4)}%`,
                }}
              />
            </div>
          </div>

          {/* MARGIN & BREAK-EVEN */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">MARGIN</div>
              <div className="text-sm font-bold text-black">
                {margin > 0 ? "+" : ""}
                {margin}%
              </div>
            </div>
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">BREAK-EVEN</div>
              <div className="text-sm font-bold text-black">{breakEven}%</div>
            </div>
          </div>

          <div className="pt-2">
            {!isInTicket ? (
              <button
                onClick={() => addMatchToTicket(prediction.id)}
                className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm text-white flex items-center justify-center gap-2"
              >
                <FaPlus size={12} /> Add to Ticket
              </button>
            ) : (
              <button
                disabled
                className="w-full px-3 py-2 bg-gray-400 rounded text-sm text-white flex items-center justify-center gap-2 cursor-not-allowed"
              >
                <FaCheckCircle size={12} /> Already in Ticket
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const TicketMatchCard = ({ match, isPastDay, onRemove }) => {
    const margin = calculateMargin(match.odd, match.win_probability);
    const breakEven = calculateBreakEven(match.odd);
    const displayValueScore = Math.abs(margin);
    const isPositiveMargin = margin > 0;
    const finalHomeGoals = match.goals_home || match.home_goals;
    const finalAwayGoals = match.goals_away || match.away_goals;
    const hasFinalResult = finalHomeGoals !== null && finalAwayGoals !== null;

    const finalScore = hasFinalResult
      ? `${finalHomeGoals} - ${finalAwayGoals}`
      : null;

    const isMatchFinished =
      match.match_status &&
      ["FT", "AET", "PEN", "FT_PEN"].includes(match.match_status);

    const getValueScoreBarColor = () => {
      if (margin >= 15) return "bg-green-500";
      if (margin >= 10) return "bg-green-400";
      if (margin >= 5) return "bg-yellow-400";
      return "bg-yellow-300";
    };

    const getMatchOutcomeBadge = () => {
      if (!match.outcome || match.outcome === "pending") return null;

      const config = {
        won: { color: "bg-green-600", text: "WON" },
        lost: { color: "bg-red-500", text: "LOST" },
        void: { color: "bg-gray-600", text: "VOID" },
      };

      const outcomeConfig = config[match.outcome] || {
        color: "bg-gray-400",
        text: match.outcome,
      };

      return (
        <span
          className={`px-2 py-1 rounded text-xs text-white ${outcomeConfig.color}`}
        >
          {outcomeConfig.text}
        </span>
      );
    };

    // Formatează ora meciului
    const matchTime = match.match_date ? formatTime(match.match_date) : "N/A";

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold bg-gray-100 text-black px-2 py-1 rounded">
              {match.league || "Unknown"}
            </span>
            <span className="text-xs text-black">#{match.rank || 1}</span>
            <span className="text-xs text-black">⏰ {matchTime}</span>
            {match.match_status && match.match_status !== "NS" && (
              <span className="text-xs font-semibold bg-gray-200 text-black px-2 py-1 rounded">
                {match.match_status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {getMatchOutcomeBadge()}
            {!isPastDay && !hasFinalResult && (
              <button
                onClick={() => onRemove(match.id)}
                className="p-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded"
                title="Remove from ticket"
              >
                <FaTrash size={12} />
              </button>
            )}
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-center flex-1">
              <h3 className="font-bold text-black text-sm truncate">
                {match.home_team}
              </h3>
            </div>

            <div className="mx-2 flex flex-col items-center min-w-[60px]">
              {hasFinalResult ? (
                <>
                  {/* Afișează scorul final */}
                  <div className="text-2xl font-bold text-black">
                    {finalHomeGoals} - {finalAwayGoals}
                  </div>
                  <div className="text-xs text-black mt-1">
                    {match.match_status || "FT"}
                  </div>
                </>
              ) : isMatchFinished ? (
                <>
                  {/* Meci terminat dar fără scor înregistrat */}
                  <div className="text-lg font-bold text-black">N/A - N/A</div>
                  <div className="text-xs text-black">{match.match_status}</div>
                </>
              ) : (
                /* Meci încă nu s-a jucat sau este în desfășurare */
                <div className="px-2 py-1 bg-gray-100 rounded">
                  <span className="text-xs font-bold text-black">VS</span>
                </div>
              )}
            </div>

            <div className="text-center flex-1">
              <h3 className="font-bold text-black text-sm truncate">
                {match.away_team}
              </h3>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="text-center mb-1">
            <div className="text-sm font-semibold text-black">
              {match.market_type}
            </div>
            <div className="text-sm font-bold text-black mt-1">
              {match.selection}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">ODD</div>
              <div className="text-lg font-bold text-black">{match.odd}</div>
            </div>
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">WIN %</div>
              <div className="text-lg font-bold text-black">
                {match.win_probability}%
              </div>
            </div>
          </div>

          <div className="space-y-1 pt-1">
            <div className="flex justify-between text-xs">
              <span className="font-semibold text-black">VALUE SCORE</span>
              <span className="font-bold text-black">
                {displayValueScore.toFixed(1)}
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`${getValueScoreBarColor()} h-full transition-all duration-300`}
                style={{
                  width: `${Math.min(100, displayValueScore * 4)}%`,
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">MARGIN</div>
              <div
                className={`text-sm font-bold ${
                  margin > 0 ? "text-black" : "text-black"
                }`}
              >
                {margin > 0 ? "+" : ""}
                {margin}%
              </div>
            </div>
            <div className="bg-gray-100 p-2 rounded text-center">
              <div className="text-xs text-black">BREAK-EVEN</div>
              <div className="text-sm font-bold text-black">{breakEven}%</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const DailyTicketCard = ({ ticket }) => {
    if (!ticket) return null;

    const isPastDay = ticket.day_offset < 0;
    const isTicketFinished =
      ticket.status === "won" || ticket.status === "lost";
    const isPublished = ticket.status === "published";
    const isReadOnly = isPastDay || isTicketFinished;

    return (
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6 shadow-sm">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              Daily Value Snapshot
            </h3>
            <div className="text-sm text-gray-600 mt-1">
              Status:{" "}
              <span className="font-semibold">
                {ticket.status.toUpperCase()}
              </span>
              {isPublished && (
                <span className="ml-2 text-blue-600">
                  <FaUpload className="inline mr-1" size={12} />
                  Published
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(ticket.status)}
            <span className="text-sm text-gray-600">
              {ticket.match_count || 0} matches
            </span>
          </div>
        </div>

        {/* Ticket Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-purple-100 rounded-lg p-4">
            <div className="text-sm text-black">Total Odd</div>
            <div
              className={`text-2xl font-bold ${
                ticket.total_odd >= 4 && ticket.total_odd <= 6
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {parseFloat(ticket.total_odd).toFixed(2)}
            </div>
          </div>

          <div className="bg-white border border-purple-100 rounded-lg p-4">
            <div className="text-sm text-black">Win Chance</div>
            <div className="text-2xl font-bold text-blue-600">
              {parseFloat(ticket.total_probability).toFixed(1)}%
            </div>
          </div>

          <div className="bg-white border border-purple-100 rounded-lg p-4">
            <div className="text-sm text-black">Potential Win</div>
            <div className="text-2xl font-bold text-green-600">
              ${parseFloat(ticket.potential_win).toFixed(2)}
            </div>
            <div className="text-xs text-black">
              Stake: ${ticket.stake || 10}
            </div>
          </div>

          <div className="bg-white border border-purple-100 rounded-lg p-4">
            <div className="text-sm text-black">Created</div>
            <div className="text-lg font-bold text-black">
              {new Date(ticket.created_at).toLocaleDateString()}
            </div>
            {ticket.published_at && (
              <div className="text-xs text-black">
                Published: {new Date(ticket.published_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        {/* Matches in Ticket */}
        {ticket.matches && ticket.matches.length > 0 && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-bold text-gray-900">
                Matches in Ticket ({ticket.matches.length})
              </h4>
              {/* Informație doar pentru biletele unpublished */}
              {!isPastDay && !isPublished && (
                <span className="text-sm text-gray-600">
                  <FaTrash className="inline mr-1" size={12} />
                  Click trash icon to remove matches
                </span>
              )}
              {/* Informație pentru biletele published */}
              {!isPastDay && isPublished && (
                <span className="text-sm text-blue-600">
                  <FaUpload className="inline mr-1" size={12} />
                  Ticket is published - matches cannot be modified
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {ticket.matches.map((match, index) => {
                const matchTime = match.match_date
                  ? formatTime(match.match_date)
                  : "N/A";
                const margin = calculateMargin(
                  match.odd,
                  match.win_probability,
                );
                const displayValueScore = Math.abs(margin);
                const finalHomeGoals = match.goals_home || match.home_goals;
                const finalAwayGoals = match.goals_away || match.away_goals;
                const hasFinalResult =
                  finalHomeGoals !== null && finalAwayGoals !== null;

                const getValueScoreBarColor = () => {
                  if (margin >= 15) return "bg-green-500";
                  if (margin >= 10) return "bg-green-400";
                  if (margin >= 5) return "bg-yellow-400";
                  return "bg-yellow-300";
                };

                return (
                  <div
                    key={match.id}
                    className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-all relative"
                  >
                    {/* BUTON DE ȘTERGERE - Doar pentru biletele DRAFT/UNPUBLISHED */}
                    {!isPastDay && !isPublished && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            window.confirm(
                              `Remove ${match.home_team} vs ${match.away_team} from ticket?`,
                            )
                          ) {
                            removeMatchFromTicket(match.id);
                          }
                        }}
                        className="absolute top-3 right-3 p-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded z-10 transition-colors"
                        title="Remove from ticket"
                      >
                        <FaTrash size={12} />
                      </button>
                    )}

                    {/* Conținut card - cu padding pentru buton */}
                    <div
                      className={isPublished ? "" : "pr-10"} // Adaugă padding doar dacă butonul există
                      onClick={() => openMatchPage(match.match_id)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-semibold bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {match.league?.substring(0, 12)}
                          </span>
                          <span className="text-xs text-gray-500">
                            #{index + 1}
                          </span>
                        </div>

                        <div className="text-right">
                          <div className="text-xs font-semibold text-gray-700">
                            ⏰ {matchTime}
                          </div>
                          {match.outcome && match.outcome !== "pending" && (
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold text-white mt-1 inline-block ${
                                match.outcome === "won"
                                  ? "bg-green-500"
                                  : match.outcome === "lost"
                                    ? "bg-red-500"
                                    : "bg-gray-500"
                              }`}
                            >
                              {match.outcome.toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Echipe și scor */}
                      <div className="mb-3">
                        <div className="flex justify-between items-center mb-2">
                          <div className="text-sm font-semibold text-gray-800 truncate max-w-[40%]">
                            {match.home_team}
                          </div>

                          <div className="mx-1 flex flex-col items-center min-w-[50px]">
                            {hasFinalResult ? (
                              <>
                                <div className="text-base font-bold text-gray-900">
                                  {finalHomeGoals} - {finalAwayGoals}
                                </div>
                                <div className="text-[10px] text-gray-600">
                                  {match.match_status || "FT"}
                                </div>
                              </>
                            ) : (
                              <div className="text-xs font-bold text-gray-500">
                                VS
                              </div>
                            )}
                          </div>

                          <div className="text-sm font-semibold text-gray-800 truncate max-w-[40%] text-right">
                            {match.away_team}
                          </div>
                        </div>

                        {/* Market și Selection */}
                        <div className="text-center mb-2">
                          <div className="text-xs font-semibold text-gray-600">
                            {match.market_type}
                          </div>
                          <div className="text-xs font-bold text-gray-800">
                            {match.selection}
                          </div>
                        </div>

                        {/* ODD și WIN% */}
                        <div className="flex justify-between text-xs mb-1">
                          <div className="flex items-center gap-1">
                            <span className="text-gray-600">ODD:</span>
                            <span className="font-bold text-gray-800">
                              {match.odd}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-gray-600">WIN%:</span>
                            <span className="font-bold text-gray-800">
                              {match.win_probability}%
                            </span>
                          </div>
                        </div>

                        {/* VALUE SCORE bar */}
                        <div className="space-y-1 mt-2">
                          <div className="flex justify-between text-[10px]">
                            <span className="font-semibold text-gray-600">
                              VALUE SCORE
                            </span>
                            <span className="font-bold text-gray-800">
                              {displayValueScore.toFixed(1)}
                            </span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`${getValueScoreBarColor()} h-full transition-all duration-300`}
                              style={{
                                width: `${Math.min(
                                  100,
                                  displayValueScore * 4,
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Buttons - LOGICĂ NOUĂ */}
        {!isReadOnly ? (
          <div className="flex flex-wrap gap-3">
            {/* Pentru DRAFT: Publish + Regenerate + Delete */}
            {ticket.status === "draft" && (
              <>
                <button
                  onClick={() => publishDailyTicket(ticket.day_offset)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white flex items-center gap-2"
                >
                  <FaUpload /> Publish Ticket
                </button>
                <button
                  onClick={() => generateDailyTicket(ticket.day_offset)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white flex items-center gap-2"
                >
                  <FaMagic /> Regenerate
                </button>
                <button
                  onClick={() => deleteDailyTicket(ticket.day_offset)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white flex items-center gap-2"
                >
                  <FaTrash /> Delete Ticket
                </button>
              </>
            )}

            {/* Pentru PUBLISHED: Doar Unpublish + Delete (NU Regenerate) */}
            {ticket.status === "published" && (
              <>
                <button
                  onClick={() => unpublishDailyTicket(ticket.day_offset)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-white flex items-center gap-2"
                >
                  <FaMinus /> Unpublish
                </button>
                <button
                  onClick={() => deleteDailyTicket(ticket.day_offset)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white flex items-center gap-2"
                >
                  <FaTrash /> Delete Ticket
                </button>
              </>
            )}
          </div>
        ) : (
          /* Pentru ticket-uri readonly (past sau finished) */
          <div className="flex flex-col gap-3">
            {ticket.status === "published" && !isTicketFinished && (
              <button
                onClick={() => verifyDailyTicketForDay(ticket.day_offset)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white flex items-center gap-2"
              >
                <FaSync /> Verify Ticket
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const filteredTasks = tasks.filter((task) => {
    /* Search */

    const matchesSearch =
      task.title
        ?.toLowerCase()
        .includes(
          filters.search.toLowerCase()
        ) ||
      task.description
        ?.toLowerCase()
        .includes(
          filters.search.toLowerCase()
        );

    /* Assigned */

    const matchesAssigned =
      filters.assigned_to === "all" ||
      task.assigned_to ===
        filters.assigned_to;

    /* Status */

    const matchesStatus =
      filters.status === "all" ||
      task.status === filters.status;

    /* Priority */

    const matchesPriority =
      filters.priority === "all" ||
      task.priority === filters.priority;

    /* Date */

    let matchesDate = true;

    if (filters.date !== "all") {
      const taskDate = new Date(
        task.created_at
      );

      const now = new Date();

      const diff =
        (now - taskDate) /
        (1000 * 60 * 60 * 24);

      switch (filters.date) {
        case "today":
          matchesDate = diff < 1;
          break;

        case "week":
          matchesDate = diff < 7;
          break;

        case "month":
          matchesDate = diff < 30;
          break;

        default:
          matchesDate = true;
      }
    }

    return (
      matchesSearch &&
      matchesAssigned &&
      matchesStatus &&
      matchesPriority &&
      matchesDate
    );
  });

  // =============== RENDER ===============
  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              ⚙️ Admin Dashboard
            </h1>
            <p className="text-gray-400 mt-2">
              Welcome, Admin {user?.username}
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-r from-blue-900/40 to-blue-800/30 border border-blue-700/40 rounded-xl p-4">
              <div className="flex items-center">
                <FaUsers className="text-blue-400 text-2xl mr-3" />
                <div>
                  <p className="text-gray-400 text-sm">Total Users</p>
                  <p className="text-white text-2xl font-bold">
                    {stats.totalUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-900/40 to-green-800/30 border border-green-700/40 rounded-xl p-4">
              <div className="flex items-center">
                <FaChartLine className="text-green-400 text-2xl mr-3" />
                <div>
                  <p className="text-gray-400 text-sm">Active Subscriptions</p>
                  <p className="text-white text-2xl font-bold">
                    {stats.activeSubscriptions}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-900/40 to-yellow-800/30 border border-yellow-700/40 rounded-xl p-4">
              <div className="flex items-center">
                <FaMoneyBillWave className="text-yellow-400 text-2xl mr-3" />
                <div>
                  <p className="text-gray-400 text-sm">Total Revenue</p>
                  <p className="text-white text-2xl font-bold">
                    ${stats.totalRevenue}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-pink-900/40 to-pink-800/30 border border-pink-700/40 rounded-xl p-4">
              <div className="flex items-center">
                <FaHistory className="text-pink-400 text-2xl mr-3" />
                <div>
                  <p className="text-gray-400 text-sm">Today's Bets</p>
                  <p className="text-white text-2xl font-bold">
                    {stats.todayBets}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-2 mb-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab("predictions")}
              className={`px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                activeTab === "predictions"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <FaFutbol className="inline mr-2" /> Analysis
            </button>
            <button
              onClick={() => setActiveTab("daily-ticket")}
              className={`px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                activeTab === "daily-ticket"
                  ? "bg-purple-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <FaTicketAlt className="inline mr-2" /> Daily Value Snapshot
            </button>
            <button
              onClick={() => setActiveTab("users")}
              className={`px-4 py-2 rounded-lg transition-all ${
                activeTab === "users"
                  ? "bg-purple-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              <FaUsers className="inline mr-2" /> Users
            </button>
            <button
              onClick={() => setActiveTab("transactions")}
              className={`px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                activeTab === "transactions"
                  ? "bg-purple-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              <FaHistory className="inline mr-2" /> Transactions
            </button>
            <button
              onClick={() => setActiveTab("user-manager")}
              className={`px-4 py-2 rounded-lg transition-all ${
                activeTab === "user-manager"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              User Manager
            </button>
            <button
              onClick={() => setActiveTab("db-manager")}
              className={`px-4 py-2 rounded-lg transition-all ${
                activeTab === "db-manager"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              DB Manager
            </button>
            <button
              onClick={() => setActiveTab("scripts")}
              className={`px-4 py-2 rounded-lg transition-all ${
                activeTab === "scripts"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              Scripts
            </button>
            <button
              onClick={() => setActiveTab("tasks")}
              className={`px-4 py-2 rounded-lg transition-all ${
                activeTab === "tasks"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              Tasks
            </button>
            <a href="/xadmin">
              <div className="w-full px-3 py-2 rounded text-sm flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white">
                New Admin
              </div>
            </a>
          </div>

          {/* Manager TAB */}
          {activeTab === "user-manager" && (
            <div className="space-y-6">
              <div className="bg-grey-900 border border-gray-700 rounded-xl p-4 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-200">
                    Create User
                  </h2>
                </div>

                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full p-2 rounded-lg bg-gray-800 border border-gray-600 text-white"
                  />

                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-2 rounded-lg bg-gray-800 border border-gray-600 text-white"
                  />

                  <input
                    type="text"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-2 rounded-lg bg-gray-800 border border-gray-600 text-white"
                  />

                  <select
                    className="bg-gray-800 text-gray-200 border border-gray-600 rounded px-2 py-1"
                    onChange={(e) => setTier(e.target.value)}
                  >
                    <option value="trial">Trial</option>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="premium">Premium</option>
                  </select>

                  <button
                    onClick={createUser}
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg"
                  >
                    {loading ? "Creating..." : "Create"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "db-manager" && (
            <div className="space-y-6">
              <div className="bg-grey-900 border border-gray-700 rounded-xl p-4 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-200">Copy DB</h2>
                </div>

                <div className="flex flex-wrap gap-4">
                  <input
                    type="text"
                    placeholder="From"
                    value={dbFrom}
                    onChange={(e) => setDbFrom(e.target.value)}
                    className="w-32 p-2 rounded-lg bg-gray-800 border border-gray-600 text-white"
                  />

                  <input
                    type="text"
                    placeholder="To"
                    value={dbTo}
                    onChange={(e) => setDbTo(e.target.value)}
                    className="w-32 p-2 rounded-lg bg-gray-800 border border-gray-600 text-white"
                  />

                  <button
                    onClick={copyDB}
                    disabled={loading}
                    className="w-32 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg"
                  >
                    {loading ? "Copying..." : "Copy"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "scripts" && (
            <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
                  <p className="text-gray-400 mt-4">Loading...</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="py-3 px-4 text-left text-gray-400">Script</th>
                      <th className="py-3 px-4 text-left text-gray-400">Database</th>
                      <th className="py-3 px-4 text-left text-gray-400">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scriptsList.map((script) => (
                      <tr
                        key={script.path}
                        className="border-b border-gray-800 hover:bg-gray-800/50"
                      >
                        <td className="py-3 px-4 text-gray-300">{script.name}</td>
                        <td className="py-3 px-4 text-gray-300">
                          <select
                            className="bg-gray-800 text-gray-200 border border-gray-600 rounded px-2 py-1"
                            value={selectedDb[script.path]}
                            onChange={(e) =>
                              setSelectedDb({
                                ...selectedDb,
                                [script.path]: e.target.value,
                              })
                            }
                          >
                            <option value="dev">Live</option>
                            <option value="test">Test</option>
                          </select>
                        </td>

                        <td className="py-3 px-4">
                          <button
                            onClick={() => executeScript(script.path)}
                            className="bg-green-500 hover:bg-green-600 text-black font-semibold py-1 px-3 rounded"
                          >
                            Execute
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === "tasks" && (
            <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
                  <p className="text-gray-400 mt-4">Loading...</p>
                </div>
              ) : (
                <div className="min-h-screen text-white p-6">
                  <div className="max-w-5xl mx-auto">
                    {/* Header */}

                    <div className="flex items-center justify-between mb-8">
                      <h1 className="text-4xl font-bold">
                        Tasks
                      </h1>

                      <div className="text-slate-400 text-sm">
                        {tasks.length} tasks
                      </div>
                    </div>

                    {/* Create Task */}

                    <form
                      onSubmit={createTask}
                      className="
                        bg-[#1a1d24]
                        border
                        border-slate-800
                        rounded-2xl
                        p-5
                        mb-8
                        space-y-4
                      "
                    >
                      <input
                        type="text"
                        placeholder="Task title..."
                        value={form.title}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            title: e.target.value,
                          })
                        }
                        className="
                          w-full
                          bg-[#111318]
                          border
                          border-slate-700
                          rounded-xl
                          px-4
                          py-3
                          outline-none
                          focus:border-blue-500
                        "
                      />

                      <textarea
                        placeholder="Description..."
                        rows={4}
                        value={form.description}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            description: e.target.value,
                          })
                        }
                        className="
                          w-full
                          bg-[#111318]
                          border
                          border-slate-700
                          rounded-xl
                          px-4
                          py-3
                          outline-none
                          resize-none
                          focus:border-blue-500
                        "
                      />

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <select
                          value={form.priority}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              priority: e.target.value,
                            })
                          }
                          className="
                            bg-[#111318]
                            border
                            border-slate-700
                            rounded-xl
                            px-4
                            py-3
                            outline-none
                          "
                        >
                          <option value="low">
                            Low Priority
                          </option>

                          <option value="medium">
                            Medium Priority
                          </option>

                          <option value="high">
                            High Priority
                          </option>
                        </select>

                        <input
                          type="text"
                          placeholder="Assign to..."
                          value={form.assigned_to}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              assigned_to:
                                e.target.value,
                            })
                          }
                          className="
                            bg-[#111318]
                            border
                            border-slate-700
                            rounded-xl
                            px-4
                            py-3
                            outline-none
                            focus:border-blue-500
                          "
                        />

                        <button
                          type="submit"
                          disabled={loading}
                          className="
                            bg-blue-600
                            hover:bg-blue-500
                            transition
                            rounded-xl
                            font-semibold
                            py-3
                          "
                        >
                          {loading
                            ? "Creating..."
                            : "Create Task"}
                        </button>
                      </div>
                    </form>

                    {/* Tasks Filters */}

                    <div
                      className="
                        bg-[#1a1d24]
                        border
                        border-slate-800
                        rounded-2xl
                        p-4
                        mb-6
                      "
                    >
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">

                        {/* Search */}

                        <input
                          type="text"
                          placeholder="Search..."
                          value={filters.search}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              search: e.target.value,
                            })
                          }
                          className="
                            bg-[#111318]
                            border
                            border-slate-700
                            rounded-xl
                            px-4
                            py-2
                            outline-none
                            focus:border-blue-500
                          "
                        />

                        {/* Assigned */}

                        <select
                          value={filters.assigned_to}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              assigned_to: e.target.value,
                            })
                          }
                          className="
                            bg-[#111318]
                            border
                            border-slate-700
                            rounded-xl
                            px-4
                            py-2
                            outline-none
                          "
                        >
                          <option value="all">
                            Everyone
                          </option>

                          {[...new Set(
                            tasks
                              .map((t) => t.assigned_to)
                              .filter(Boolean)
                          )].map((user) => (
                            <option
                              key={user}
                              value={user}
                            >
                              {user}
                            </option>
                          ))}
                        </select>

                        {/* Status */}

                        <select
                          value={filters.status}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              status: e.target.value,
                            })
                          }
                          className="
                            bg-[#111318]
                            border
                            border-slate-700
                            rounded-xl
                            px-4
                            py-2
                          "
                        >
                          <option value="all">
                            All Statuses
                          </option>

                          <option value="new">
                            New
                          </option>

                          <option value="in_progress">
                            In Progress
                          </option>

                          <option value="done">
                            Done
                          </option>
                        </select>

                        {/* Priority */}

                        <select
                          value={filters.priority}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              priority: e.target.value,
                            })
                          }
                          className="
                            bg-[#111318]
                            border
                            border-slate-700
                            rounded-xl
                            px-4
                            py-2
                          "
                        >
                          <option value="all">
                            All Priorities
                          </option>

                          <option value="low">
                            Low
                          </option>

                          <option value="medium">
                            Medium
                          </option>

                          <option value="high">
                            High
                          </option>
                        </select>

                        {/* Date */}

                        <select
                          value={filters.date}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              date: e.target.value,
                            })
                          }
                          className="
                            bg-[#111318]
                            border
                            border-slate-700
                            rounded-xl
                            px-4
                            py-2
                          "
                        >
                          <option value="all">
                            All Time
                          </option>

                          <option value="today">
                            Today
                          </option>

                          <option value="week">
                            Last 7 Days
                          </option>

                          <option value="month">
                            Last 30 Days
                          </option>
                        </select>
                      </div>
                    </div>

                    {/* Tasks List */}

                    <div className="space-y-5">
                      {filteredTasks.map((task) => (
                        <div
                          key={task.id}
                          className="
                            bg-[#1a1d24]
                            border
                            border-slate-800
                            rounded-2xl
                            p-5
                            shadow-lg
                          "
                        >
                          <div className="flex justify-between gap-4">
                            <div className="flex-1">
                              <h2 className="text-xl font-semibold">
                                {task.title}
                              </h2>

                              {task.description && (
                                <div
                                  onClick={() => startEditing(task)}
                                  onPaste={(e) =>
                                    handlePaste(e, task.id)
                                  }
                                  className="
                                    text-slate-400
                                    mt-3
                                    whitespace-pre-wrap
                                    border
                                    border-slate-800
                                    rounded-xl
                                    p-3
                                    hover:border-slate-600
                                    transition
                                    min-h-[60px]
                                    cursor-text
                                  "
                                >
                                  {editingTaskId === task.id ? (
                                    <textarea
                                      autoFocus
                                      value={editDescription}
                                      onChange={(e) =>
                                        setEditDescription(e.target.value)
                                      }
                                      onBlur={() =>
                                        saveDescription(task.id)
                                      }
                                      className="
                                        w-full
                                        bg-transparent
                                        outline-none
                                        text-slate-300
                                        resize-none
                                      "
                                      rows={4}
                                    />
                                  ) : task.description ? (
                                    task.description
                                  ) : (
                                    "Click to edit or press Ctrl+V to add screenshot"
                                  )}
                                </div>
                              )}

                              {editingTaskId === task.id && (
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={() =>
                                      saveDescription(task.id)
                                    }
                                    className="
                                      px-3
                                      py-1
                                      bg-green-700
                                      rounded-lg
                                      text-sm
                                    "
                                  >
                                    Save
                                  </button>

                                  <button
                                    onClick={() =>
                                      setEditingTaskId(null)
                                    }
                                    className="
                                      px-3
                                      py-1
                                      bg-slate-700
                                      rounded-lg
                                      text-sm
                                    "
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}

                              {task.attachments?.length > 0 && (
                                <div className="flex flex-wrap gap-3 mt-4">
                                  {task.attachments.map((file) => (
                                    <div
                                      key={file.id}
                                      className="relative group w-28 h-28"
                                    >
                                    <img
                                      key={file.id}
                                      src={`${process.env.REACT_APP_BACKEND_URL}/uploads/task_attachments/${file.filename}`}
                                      alt=""
                                      className="
                                        rounded-lg
                                        border
                                        border-slate-700
                                        hover:scale-[1.02]
                                        transition
                                        cursor-pointer
                                      "
                                      onClick={() =>
                                        setSelectedImage(
                                          `${process.env.REACT_APP_BACKEND_URL}/uploads/task_attachments/${file.filename}`
                                        )
                                      }
                                    />
                                    <button
                                      onClick={() =>
                                        deleteAttachment(
                                          task.id,
                                          file.id
                                        )
                                      }
                                      className="
                                        absolute
                                        top-1
                                        right-1
                                        bg-black/70
                                        text-white
                                        text-xs
                                        w-6 h-6
                                        flex items-center justify-center
                                        rounded
                                        opacity-0
                                        group-hover:opacity-100
                                        transition
                                        hover:bg-red-600
                                      "
                                    >
                                      ✕
                                    </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <div className="mt-5">
                                <div className="space-y-3">
                                  {task.comments?.map((comment) => (
                                    <div
                                      key={comment.id}
                                      className="
                                        bg-[#111318]
                                        border
                                        border-slate-800
                                        rounded-xl
                                        p-3
                                      "
                                    >
                                      <div className="flex justify-between items-start gap-3">
                                        <div>
                                          <div className="text-sm text-slate-300 font-medium">
                                            {comment.created_by}
                                          </div>

                                          <div className="text-slate-400 text-sm mt-1 whitespace-pre-wrap">
                                            {comment.comment}
                                          </div>
                                        </div>

                                        <button
                                          onClick={() =>
                                            deleteComment(
                                              task.id,
                                              comment.id
                                            )
                                          }
                                          className="
                                            text-slate-500
                                            hover:text-red-400
                                            transition
                                            text-xs
                                          "
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                <div className="flex gap-2 mt-4">
                                  <input
                                    type="text"
                                    placeholder="Add comment..."
                                    value={
                                      commentInputs[task.id] || ""
                                    }
                                    onChange={(e) =>
                                      setCommentInputs((prev) => ({
                                        ...prev,
                                        [task.id]: e.target.value,
                                      }))
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        addComment(task.id);
                                      }
                                    }}
                                    className="
                                      flex-1
                                      bg-[#111318]
                                      border
                                      border-slate-700
                                      rounded-xl
                                      px-4
                                      py-2
                                      outline-none
                                      focus:border-blue-500
                                      text-sm
                                    "
                                  />

                                  <button
                                    onClick={() =>
                                      addComment(task.id)
                                    }
                                    disabled={submittingComment}
                                    className="
                                      px-4
                                      py-2
                                      bg-blue-600
                                      hover:bg-blue-500
                                      rounded-xl
                                      text-sm
                                      transition
                                    "
                                  >
                                    Send
                                  </button>
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-2 mt-4">
                                <span
                                  onClick={() =>
                                    updateTask(task.id, {
                                      status: cycleStatus(task.status),
                                    })
                                  }
                                  className={`
                                    px-3
                                    py-1
                                    rounded-full
                                    text-xs
                                    font-medium
                                    cursor-pointer
                                    hover:opacity-80
                                    transition
                                    ${statusColor(task.status)}
                                  `}
                                >
                                  {task.status.replace("_", " ")}
                                </span>

                                <span
                                  onClick={() =>
                                    updateTask(task.id, {
                                      priority: cyclePriority(
                                        task.priority
                                      ),
                                    })
                                  }
                                  className={`
                                    px-3
                                    py-1
                                    rounded-full
                                    text-xs
                                    border
                                    cursor-pointer
                                    hover:opacity-80
                                    transition
                                    ${priorityColor(task.priority)}
                                  `}
                                >
                                  {task.priority}
                                </span>

                                {editingAssignedId === task.id ? (
                                  <input
                                    autoFocus
                                    value={assignedValue}
                                    onChange={(e) =>
                                      setAssignedValue(e.target.value)
                                    }
                                    onBlur={() =>
                                      saveAssignedTo(task.id)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        saveAssignedTo(task.id);
                                      }
                                    }}
                                    className="
                                      px-3
                                      py-1
                                      rounded-full
                                      text-xs
                                      bg-[#111318]
                                      border
                                      border-slate-700
                                      text-slate-300
                                      outline-none
                                    "
                                    placeholder="Assign to..."
                                  />
                                ) : (
                                  <span
                                    onClick={() =>
                                      startEditAssigned(task)
                                    }
                                    className="
                                      px-3
                                      py-1
                                      rounded-full
                                      text-xs
                                      bg-purple-500/20
                                      text-purple-300
                                      border
                                      border-purple-500/30
                                      cursor-pointer
                                      hover:opacity-80
                                      transition
                                    "
                                  >
                                    {task.assigned_to
                                      ? `Assigned to ${task.assigned_to}`
                                      : "Unassigned"}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-col items-end">
                              <button
                                onClick={() =>
                                  deleteTask(task.id)
                                }
                                className="
                                  text-slate-500
                                  hover:text-red-400
                                  transition
                                  text-lg
                                "
                              >
                                ✕
                              </button>

                              <div
                                className="
                                  text-xs
                                  text-slate-500
                                  mt-auto
                                "
                              >
                                Created by
                                <br />

                                <span className="text-slate-300">
                                  {task.created_by ||
                                    "Unknown"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {!tasks.length && (
                      <div
                        className="
                          text-center
                          text-slate-500
                          mt-16
                        "
                      >
                        No tasks yet
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedImage && (
                <div
                  className="
                    fixed
                    inset-0
                    bg-black/80
                    flex
                    items-center
                    justify-center
                    z-50
                    p-4
                  "
                  onClick={() =>
                    setSelectedImage(null)
                  }
                >
                  <img
                    src={selectedImage}
                    alt=""
                    className="
                      max-w-full
                      max-h-full
                      rounded-xl
                      shadow-2xl
                      border
                      border-slate-700
                    "
                  />

                  <button
                    onClick={() =>
                      setSelectedImage(null)
                    }
                    className="
                      absolute
                      top-5
                      right-5
                      text-white
                      text-2xl
                      bg-black/40
                      px-3
                      py-1
                      rounded-lg
                      hover:bg-black/60
                      transition
                    "
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === "predictions" && (
            <div className="space-y-6">
              {/* Day Management */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    Day Management
                  </h2>
                  <div className="flex items-center gap-4">
                    {getActiveSelected() !== 0 && (
                      <button
                        onClick={goToTodayForActive}
                        className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                        title="Go to Today"
                      >
                        Today
                      </button>
                    )}
                    {selectedDay < 0 && (
                      <button
                        onClick={verifyPredictions}
                        disabled={verifying}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 text-white flex items-center gap-2"
                      >
                        <FaSync className={verifying ? "animate-spin" : ""} />
                        {verifying ? "Verifying..." : "Verify Analysis"}
                      </button>
                    )}
                    <div className="text-sm text-gray-600">
                      Status:{" "}
                      <span
                        className={
                          getDayConfig(selectedDay).status === "show"
                            ? "text-green-600 font-semibold"
                            : "text-red-600 font-semibold"
                        }
                      >
                        {getDayConfig(selectedDay).status === "show"
                          ? "Visible"
                          : "Hidden"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Day Selector: full range from -30 .. +2 with arrows outside */}
                <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={() => setSelectedDay((d) => Math.max(d - 1, -30))}
                    className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200"
                    aria-label="previous day"
                    title="Previous day (up to 30 days back)"
                    disabled={selectedDay <= -30}
                  >
                    ◀
                  </button>

                  <div className="relative w-full overflow-hidden">
                    {/* reserve space below cards so scrollbar doesn't overlap them */}
                    <div
                      ref={predictionsScrollerRef}
                      className="flex gap-3 overflow-x-auto py-1"
                      style={{ paddingBottom: 12, scrollbarGutter: "stable" }}
                    >
                      {Array.from({
                        length: MAX_DAY_OFFSET - MIN_DAY_OFFSET + 1,
                      }).map((_, i) => {
                        const offset = MIN_DAY_OFFSET + i; // -30 .. +2
                        const config = getDayConfig(offset);
                        const isSelected = selectedDay === offset;
                        const isPastDay = offset < 0;
                        const isToday = offset === 0;
                        // build class: today gets light green, selected today stronger green
                        let cardClass = "";
                        if (isSelected) {
                          cardClass = isToday
                            ? "border-green-600 bg-green-100"
                            : isPastDay
                              ? "border-purple-500 bg-purple-50"
                              : "border-blue-500 bg-blue-50";
                        } else {
                          cardClass = isToday
                            ? "border-green-300 bg-green-50 hover:bg-green-100"
                            : "border-gray-200 bg-white hover:bg-gray-50";
                        }
                        return (
                          <div
                            data-offset={offset}
                            key={offset}
                            className={`min-w-[220px] p-4 rounded-lg border cursor-pointer transition-all ${cardClass}`}
                            onClick={() => setSelectedDay(offset)}
                          >
                            <div className="flex justify-between items-center mb-2">
                              <h3 className="font-semibold text-gray-900">
                                {getDayDisplayName(offset)}
                              </h3>
                            </div>

                            <div className="flex flex-col gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  generatePredictions(offset);
                                }}
                                disabled={generating && offset === selectedDay}
                                className={`w-full px-3 py-2 rounded text-sm flex items-center justify-center gap-2 ${
                                  offset >= 0
                                    ? "bg-green-600 hover:bg-green-700 text-white"
                                    : "bg-gray-600 hover:bg-gray-700 text-white"
                                } disabled:opacity-50`}
                              >
                                {offset >= 0 ? (
                                  <>
                                    <FaMagic
                                      size={12}
                                      className={
                                        generating ? "animate-spin" : ""
                                      }
                                    />
                                    Generate
                                  </>
                                ) : (
                                  <>
                                    <FaEye size={12} />
                                    Show
                                  </>
                                )}
                              </button>

                              {offset >= 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    publishAllForDay(offset);
                                  }}
                                  disabled={publishing}
                                  className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm disabled:opacity-50 flex items-center justify-center gap-2 text-white"
                                >
                                  <FaUpload size={12} /> Publish All
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    onClick={() => setSelectedDay((d) => Math.min(d + 1, 2))}
                    className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200"
                    aria-label="next day"
                    title="Next day (max +2 days)"
                    disabled={selectedDay >= 2}
                  >
                    ▶
                  </button>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Analysis for {getDayDisplayName(selectedDay)}
                      {selectedDay < 0 && (
                        <span className="ml-2 text-sm text-purple-600 font-semibold">
                          (Past Day - Verification Available)
                        </span>
                      )}
                    </h2>
                    <p className="text-gray-600">
                      {predictions.length} predictions
                    </p>
                  </div>

                  <div className="text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1 mr-3">
                      <div className="w-3 h-3 bg-green-600 rounded"></div>
                      Won:{" "}
                      {predictions.filter((p) => p.status === "won").length}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <div className="w-3 h-3 bg-red-500 rounded"></div>
                      Lost:{" "}
                      {predictions.filter((p) => p.status === "lost").length}
                    </span>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading predictions...</p>
                  </div>
                ) : predictions.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FaDatabase className="text-4xl mx-auto mb-4 opacity-50" />
                    <p className="text-lg mb-2">
                      No predictions for {getDayDisplayName(selectedDay)}
                    </p>
                    <p className="text-sm mb-4">
                      {selectedDay >= 0
                        ? "Generate predictions to get started"
                        : "No historical predictions available"}
                    </p>
                    {selectedDay >= 0 && (
                      <button
                        onClick={() => generatePredictions(selectedDay)}
                        disabled={generating}
                        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 text-white flex items-center gap-2 mx-auto"
                      >
                        <FaMagic className={generating ? "animate-spin" : ""} />
                        {generating ? "Generating..." : "Generate Analysis"}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {predictions.map((prediction) => (
                      <PredictionCard
                        key={prediction.id}
                        prediction={prediction}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Daily Value Snapshot TAB */}
          {activeTab === "daily-ticket" && (
            <div className="space-y-6">
              {/* Daily Value Snapshot Stats */}
              {ticketStats && (
                <div className="bg-gradient-to-r from-purple-900/40 to-pink-800/30 border border-purple-700/40 rounded-xl p-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-sm text-purple-300">
                        Total Tickets
                      </div>
                      <div className="text-3xl font-bold text-white">
                        {ticketStats.stats.total_tickets || 0}
                      </div>
                      <div className="text-xs text-purple-300">
                        Won: {ticketStats.stats.won || 0} | Lost:{" "}
                        {ticketStats.stats.lost || 0}
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="text-sm text-purple-300">Win Rate</div>
                      <div className="text-3xl font-bold text-white">
                        {ticketStats.stats.won &&
                        ticketStats.stats.total_tickets
                          ? Math.round(
                              (ticketStats.stats.won /
                                ticketStats.stats.total_tickets) *
                                100,
                            )
                          : 0}
                        %
                      </div>
                      <div className="text-xs text-purple-300">
                        Based on completed tickets
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="text-sm text-purple-300">Avg. Odd</div>
                      <div className="text-3xl font-bold text-white">
                        {ticketStats.stats.avg_odd
                          ? parseFloat(ticketStats.stats.avg_odd).toFixed(2)
                          : "0.00"}
                      </div>
                      <div className="text-xs text-purple-300">
                        Target: 4 - 6
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="text-sm text-purple-300">
                        Avg. Win Chance
                      </div>
                      <div className="text-3xl font-bold text-white">
                        {ticketStats.stats.avg_probability
                          ? parseFloat(
                              ticketStats.stats.avg_probability,
                            ).toFixed(1)
                          : "0"}
                        %
                      </div>
                      <div className="text-xs text-purple-300">
                        Combined probability
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    Daily Value Snapshot Management
                  </h2>
                  <div className="flex items-center gap-4">
                    {getActiveSelected() !== 0 && (
                      <button
                        onClick={goToTodayForActive}
                        className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                        title="Go to Today"
                      >
                        Today
                      </button>
                    )}
                    {/* Day Management for Daily Value Snapshot */}
                    {selectedTicketDay < 0 && (
                      <button
                        onClick={() =>
                          verifyDailyTicketForDay(selectedTicketDay)
                        }
                        disabled={verifyingTickets}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 text-white flex items-center gap-2"
                      >
                        <FaSync
                          className={verifyingTickets ? "animate-spin" : ""}
                        />
                        {verifyingTickets ? "Verifying..." : "Verify Ticket"}
                      </button>
                    )}
                    <div className="text-sm text-gray-600">
                      Status:{" "}
                      <span
                        className={
                          getDailyTicketConfig(selectedTicketDay).status ===
                          "show"
                            ? "text-green-600 font-semibold"
                            : "text-red-600 font-semibold"
                        }
                      >
                        {getDailyTicketConfig(selectedTicketDay).status ===
                        "show"
                          ? "Visible"
                          : "Hidden"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Day Selector: full range -30..+2 with arrows outside */}
                <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={() =>
                      setSelectedTicketDay((d) => Math.max(d - 1, -30))
                    }
                    className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200"
                    aria-label="previous day"
                    title="Previous day (up to 30 days back)"
                    disabled={selectedTicketDay <= -30}
                  >
                    ◀
                  </button>

                  <div className="relative w-full overflow-hidden">
                    {/* reserve space below cards so scrollbar doesn't overlap them */}
                    <div
                      ref={ticketsScrollerRef}
                      className="flex gap-3 overflow-x-auto py-1"
                      style={{ paddingBottom: 12, scrollbarGutter: "stable" }}
                    >
                      {Array.from({
                        length: MAX_DAY_OFFSET - MIN_DAY_OFFSET + 1,
                      }).map((_, i) => {
                        const offset = MIN_DAY_OFFSET + i; // -30 .. +2
                        const config = getDailyTicketConfig(offset);
                        const isSelected = selectedTicketDay === offset;
                        const isPastDay = offset < 0;
                        const isToday = offset === 0;
                        let cardClass = "";
                        if (isSelected) {
                          cardClass = isToday
                            ? "border-green-600 bg-green-100"
                            : isPastDay
                              ? "border-purple-500 bg-purple-50"
                              : "border-blue-500 bg-blue-50";
                        } else {
                          cardClass = isToday
                            ? "border-green-300 bg-green-50 hover:bg-green-100"
                            : "border-gray-200 bg-white hover:bg-gray-50";
                        }
                        return (
                          <div
                            data-offset={offset}
                            key={offset}
                            className={`min-w-[220px] p-4 rounded-lg border cursor-pointer transition-all ${cardClass}`}
                            onClick={() => setSelectedTicketDay(offset)}
                          >
                            <div className="flex justify-between items-center mb-2">
                              <h3 className="font-semibold text-gray-900">
                                {getDayDisplayName(offset)}
                              </h3>
                            </div>

                            <div className="flex flex-col gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  generateDailyTicketPreview(offset);
                                }}
                                disabled={
                                  loadingDailyTicket &&
                                  offset === selectedTicketDay
                                }
                                className={`w-full px-3 py-2 rounded text-sm flex items-center justify-center gap-2 ${
                                  offset >= 0
                                    ? "bg-green-600 hover:bg-green-700 text-white"
                                    : "bg-gray-600 hover:bg-gray-700 text-white"
                                } disabled:opacity-50`}
                              >
                                {offset >= 0 ? (
                                  <>
                                    <FaMagic
                                      size={12}
                                      className={
                                        loadingDailyTicket ? "animate-spin" : ""
                                      }
                                    />
                                    {loadingDailyTicket
                                      ? "Generating..."
                                      : "Generate"}
                                  </>
                                ) : (
                                  <>
                                    <FaEye size={12} />
                                    Show
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    onClick={() =>
                      setSelectedTicketDay((d) => Math.min(d + 1, 2))
                    }
                    className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200"
                    aria-label="next day"
                    title="Next day (max +2 days)"
                    disabled={selectedTicketDay >= 2}
                  >
                    ▶
                  </button>
                </div>
              </div>

              {selectedTicketDay >= 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        Available Analysis for{" "}
                        {getDayDisplayName(selectedTicketDay)}
                      </h2>
                      <p className="text-gray-600">
                        Select predictions to add to your Daily Value Snapshot
                      </p>
                    </div>
                    <div className="text-sm text-gray-600">
                      {filteredPredictions.length} predictions available
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaSearch className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        placeholder="Search by team or league..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <select
                      value={selectedMarket}
                      onChange={(e) => setSelectedMarket(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Markets</option>
                      <option value="Match Winner">Match Winner</option>
                      <option value="Goals Over/Under">Goals Over/Under</option>
                      <option value="Both Teams To Score">
                        Both Teams To Score
                      </option>
                    </select>
                  </div>

                  {loadingAvailablePredictions ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
                      <p className="text-gray-600 mt-4">
                        Loading available predictions...
                      </p>
                    </div>
                  ) : filteredPredictions.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <FaDatabase className="text-4xl mx-auto mb-4 opacity-50" />
                      <p className="text-lg mb-2">
                        No available predictions for{" "}
                        {getDayDisplayName(selectedTicketDay)}
                      </p>
                      <p className="text-sm mb-4">
                        {selectedTicketDay >= 0
                          ? "Generate and publish analysis first, or all analysis are already in the ticket"
                          : "No analysis available for past days"}
                      </p>
                      {selectedTicketDay >= 0 && (
                        <button
                          onClick={() => generatePredictions(selectedTicketDay)}
                          disabled={generating}
                          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 text-white flex items-center gap-2 mx-auto"
                        >
                          <FaMagic
                            className={generating ? "animate-spin" : ""}
                          />
                          {generating ? "Generating..." : "Generate Analysis"}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredPredictions.map((prediction) => (
                        <AvailablePredictionCard
                          key={prediction.id}
                          prediction={prediction}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Daily Value Snapshot Display */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Daily Value Snapshot for{" "}
                      {getDayDisplayName(selectedTicketDay)}
                      {selectedTicketDay < 0 && (
                        <span className="ml-2 text-sm text-purple-600 font-semibold">
                          (Past Day - Verification Available)
                        </span>
                      )}
                    </h2>
                    {dailyTicket && (
                      <p className="text-gray-600">
                        Status: {dailyTicket.status.toUpperCase()} | Matches:{" "}
                        {dailyTicket.match_count || 0} | Total Odd:{" "}
                        {parseFloat(dailyTicket.total_odd).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>

                {loadingDailyTicketDisplay ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
                    <p className="text-gray-600 mt-4">
                      Loading Daily Value Snapshot...
                    </p>
                  </div>
                ) : !dailyTicket ? (
                  <div className="text-center py-12 text-gray-500">
                    <FaTicketAlt className="text-4xl mx-auto mb-4 opacity-50" />
                    <p className="text-lg mb-2">
                      No Daily Value Snapshot for{" "}
                      {getDayDisplayName(selectedTicketDay)}
                    </p>
                    <p className="text-sm mb-4">
                      {selectedTicketDay >= 0
                        ? "Generate a Daily Value Snapshot to get started"
                        : "No historical ticket available"}
                    </p>
                    {selectedTicketDay >= 0 && (
                      <button
                        onClick={() => generateDailyTicket(selectedTicketDay)}
                        disabled={generatingTicket}
                        className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 text-white flex items-center gap-2 mx-auto"
                      >
                        <FaMagic
                          className={generatingTicket ? "animate-spin" : ""}
                        />
                        {generatingTicket
                          ? "Generating..."
                          : "Generate Daily Value Snapshot"}
                      </button>
                    )}
                  </div>
                ) : (
                  <DailyTicketCard ticket={dailyTicket} />
                )}
              </div>
            </div>
          )}

          {/* USERS TAB */}
          {activeTab === "users" && (
            <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
                  <p className="text-gray-400 mt-4">Loading users...</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="py-3 px-4 text-left text-gray-400">ID</th>
                      <th className="py-3 px-4 text-left text-gray-400">
                        Username
                      </th>
                      <th className="py-3 px-4 text-left text-gray-400">
                        Email
                      </th>
                      <th className="py-3 px-4 text-left text-gray-400">
                        Balance
                      </th>
                      <th className="py-3 px-4 text-left text-gray-400">
                        Status
                      </th>
                      <th className="py-3 px-4 text-left text-gray-400">
                        Admin
                      </th>
                      <th className="py-3 px-4 text-left text-gray-400">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-gray-800 hover:bg-gray-800/50"
                      >
                        <td className="py-3 px-4 text-gray-300">{user.id}</td>
                        <td className="py-3 px-4 text-gray-300">
                          {user.username}
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          {user.email}
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          ${parseFloat(user.balance).toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              user.status === "active"
                                ? "bg-green-900/40 text-green-400"
                                : "bg-red-900/40 text-red-400"
                            }`}
                          >
                            {user.status || "active"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {user.is_admin ? (
                            <span className="px-2 py-1 rounded text-xs bg-purple-900/40 text-purple-400">
                              Admin
                            </span>
                          ) : (
                            <span className="text-gray-500">User</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex space-x-2">
                            <button
                              className={`p-1.5 rounded ${
                                user.status === "active"
                                  ? "bg-red-600 hover:bg-red-700"
                                  : "bg-green-600 hover:bg-green-700"
                              }`}
                            >
                              {user.status === "active" ? (
                                <FaLock size={14} />
                              ) : (
                                <FaUnlock size={14} />
                              )}
                            </button>
                            <button className="p-1.5 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded">
                              <FaMoneyBillWave size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* TRANSACTIONS TAB */}
          {activeTab === "transactions" && (
            <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="py-3 px-4 text-left text-gray-400">ID</th>
                      <th className="py-3 px-4 text-left text-gray-400">
                        User
                      </th>
                      <th className="py-3 px-4 text-left text-gray-400">
                        Amount
                      </th>
                      <th className="py-3 px-4 text-left text-gray-400">
                        Type
                      </th>
                      <th className="py-3 px-4 text-left text-gray-400">
                        Description
                      </th>
                      <th className="py-3 px-4 text-left text-gray-400">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr
                        key={tx.id}
                        className="border-b border-gray-800 hover:bg-gray-800/50"
                      >
                        <td className="py-3 px-4 text-gray-300">{tx.id}</td>
                        <td className="py-3 px-4 text-gray-300">
                          {tx.username}
                        </td>
                        <td
                          className={`py-3 px-4 ${
                            tx.amount >= 0 ? "text-green-400" : "text-red-400"
                          }`}
                        >
                          ${parseFloat(tx.amount).toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-gray-300">{tx.type}</td>
                        <td className="py-3 px-4 text-gray-300">
                          {tx.description}
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          {new Date(tx.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Admin;
